import { useState, useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Upload, Save, Plus, Search, MapPin, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import { MAP_CENTER } from "@/mock";
import {
  adminListarIslas, adminEditarIsla, adminBorrarIsla, adminCrearIsla, adminImportarIslas, adminExportarIslas,
} from "@/api";

const dotIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 0 0 1px rgba(0,0,0,0.25);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

const emptyForm = { nombre: "", direccion_referencia: "", lat: "", lng: "", llena: false };

export default function IslasPanel() {
  const [islas, setIslas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => adminListarIslas().then(setIslas).catch(() => toast.error("No se pudieron cargar las islas"));

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return islas;
    const q = search.trim().toLowerCase();
    return islas.filter((i) => i.nombre.toLowerCase().includes(q) || (i.direccion_referencia || "").toLowerCase().includes(q));
  }, [islas, search]);

  const selectIsla = (isla) => {
    setSelected(isla);
    setForm({
      nombre: isla.nombre,
      direccion_referencia: isla.direccion_referencia || "",
      lat: isla.lat ?? "",
      lng: isla.lng ?? "",
      llena: !!isla.llena,
    });
  };

  const handleNew = () => {
    setSelected({ id: null });
    setForm({ ...emptyForm, lat: MAP_CENTER[0], lng: MAP_CENTER[1] });
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        direccion_referencia: form.direccion_referencia,
        lat: form.lat === "" ? null : Number(form.lat),
        lng: form.lng === "" ? null : Number(form.lng),
        llena: form.llena,
      };
      if (selected?.id) {
        await adminEditarIsla(selected.id, payload);
        toast.success("Isla actualizada");
      } else {
        await adminCrearIsla(payload);
        toast.success("Isla creada");
      }
      setSelected(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo guardar la isla"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminBorrarIsla(id);
      toast.success("Isla eliminada");
      if (selected?.id === id) { setSelected(null); setForm(emptyForm); }
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo eliminar la isla"));
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await adminImportarIslas(file);
      toast.success(`${res.importadas} islas importadas/actualizadas`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo importar el Excel"));
    } finally {
      e.target.value = "";
    }
  };

  const withCoords = islas.filter((i) => i.lat != null && i.lng != null);

  return (
    <div className="space-y-4" data-testid="islas-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-heading font-semibold text-municipal">Islas de contenedores ({islas.length})</h3>
        <div className="flex-1" />
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={handleImport} data-testid="islas-panel-import-input" />
        <Button variant="outline" className="border-dorado text-dorado" onClick={() => fileRef.current?.click()} data-testid="islas-panel-import-btn">
          <Upload className="w-4 h-4 mr-2" /> Importar Excel
        </Button>
        <Button variant="outline" className="border-municipal text-municipal" onClick={adminExportarIslas} data-testid="islas-panel-export-btn">
          <Download className="w-4 h-4 mr-2" /> Exportar Excel
        </Button>
        <Button className="bg-municipal hover:bg-municipal-dark text-white" onClick={handleNew} data-testid="islas-panel-nueva-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva isla manual
        </Button>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        <div className="h-[480px] rounded-xl overflow-hidden border border-gray-200" data-testid="islas-panel-map">
          <MapContainer center={MAP_CENTER} zoom={14} style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {withCoords.map((isla) => (
              <Marker
                key={isla.id}
                position={[isla.lat, isla.lng]}
                icon={dotIcon(selected?.id === isla.id ? "#BD3742" : isla.llena ? "#dc2626" : "#5c927a")}
                eventHandlers={{ click: () => selectIsla(isla) }}
                data-testid={`islas-panel-marker-${isla.id}`}
              >
                <Tooltip direction="top">{isla.nombre}{isla.llena ? " · LLENA" : ""}</Tooltip>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 h-[480px] flex flex-col">
          {selected ? (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-municipal">{selected.id ? "Editar isla" : "Nueva isla"}</p>
              <div>
                <Label className="text-xs mb-1 block">Nombre</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="islas-panel-nombre-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Referencia / dirección</Label>
                <Input value={form.direccion_referencia} onChange={(e) => setForm({ ...form, direccion_referencia: e.target.value })} data-testid="islas-panel-direccion-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs mb-1 block">Lat</Label>
                  <Input type="number" step="0.000001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="islas-panel-lat-input" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Lng</Label>
                  <Input type="number" step="0.000001" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="islas-panel-lng-input" />
                </div>
              </div>
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <Checkbox
                  id="isla-llena"
                  checked={form.llena}
                  onCheckedChange={(v) => setForm({ ...form, llena: v })}
                  data-testid="islas-panel-llena-checkbox"
                />
                <Label htmlFor="isla-llena" className="font-normal text-xs text-red-700 cursor-pointer flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Marcar como llena temporalmente (avisa al ciudadano)
                </Label>
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" disabled={saving} onClick={handleSave} className="bg-municipal hover:bg-municipal-dark text-white flex-1" data-testid="islas-panel-guardar-btn">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> Guardar
                </Button>
                {selected.id && (
                  <Button size="sm" variant="outline" className="border-red-300 text-red-600" onClick={() => handleDelete(selected.id)} data-testid="islas-panel-eliminar-btn">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <Button size="sm" variant="ghost" className="text-gray-400 w-full" onClick={() => { setSelected(null); setForm(emptyForm); }}>
                Cancelar
              </Button>
            </div>
          ) : (
            <>
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar isla..." className="pl-8" data-testid="islas-panel-search-input" />
              </div>
              <div className="overflow-y-auto flex-1 space-y-1">
                {filtered.map((isla) => (
                  <button
                    key={isla.id}
                    onClick={() => selectIsla(isla)}
                    className="w-full text-left text-xs px-2.5 py-2 rounded-lg hover:bg-municipal/5 flex items-start gap-1.5"
                    data-testid={`islas-panel-list-item-${isla.id}`}
                  >
                    <MapPin className={`w-3 h-3 shrink-0 mt-0.5 ${isla.llena ? "text-red-500" : "text-dorado"}`} />
                    <span className={isla.llena ? "text-red-600 font-medium" : "text-gray-600"}>{isla.nombre}{isla.llena ? " (LLENA)" : ""}</span>
                  </button>
                ))}
                {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-6">Sin resultados</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
